import type { ChipProps } from '@mui/material/Chip';
import type {
  AttendanceStatus,
  Booking,
  BookingStatus,
  PaymentStatus,
  RefundStatus,
} from '../../types';

export type ReservationChipMeta = {
  label: string;
  color: ChipProps['color'];
  variant: ChipProps['variant'];
};

export function resolveBookingStatus(booking: Booking): BookingStatus {
  const rawStatus = booking.bookingStatus || booking.status;

  switch (rawStatus) {
    case 'pending_payment':
    case 'pending_validation':
    case 'confirmed':
    case 'cancelled':
    case 'expired':
    case 'failed':
      return rawStatus;
    default:
      return 'pending_validation';
  }
}

export function resolvePaymentStatus(booking: Booking): PaymentStatus {
  return booking.paymentStatus || (booking.isPaid ? 'paid' : 'pending');
}

export function resolveAttendanceStatus(booking: Booking): AttendanceStatus {
  return booking.attendanceStatus || 'expected';
}

export function resolveRefundStatus(booking: Booking): RefundStatus {
  return booking.refundStatus || 'not_applicable';
}

export function resolvePartialRefundState(booking: Booking) {
  const nestedState = String(booking.partialRefund?.status || '').trim();
  if (nestedState) return nestedState;

  const topLevelState = String(booking.partialRefundStatus || '').trim();
  if (topLevelState) return topLevelState;

  return null;
}

export function getPaymentMethodLabel(method?: Booking['paymentMethod']) {
  switch (method) {
    case 'mollie':
      return 'Carte / Mollie';
    case 'bank_transfer':
      return 'Virement';
    case 'on_site':
      return 'Sur place';
    default:
      return 'Manuel';
  }
}

export function getReservationSearchHaystack(booking: Booking) {
  return [
    booking.userName,
    booking.userEmail,
    booking.userPhone,
    booking.id,
    booking.bankTransferCommunication,
    booking.specialNeeds,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function getBookingStatusMeta(status: BookingStatus): ReservationChipMeta {
  switch (status) {
    case 'confirmed':
      return { label: 'Confirmée', color: 'success', variant: 'filled' };
    case 'pending_payment':
      return { label: 'Attente paiement', color: 'warning', variant: 'filled' };
    case 'cancelled':
      return { label: 'Annulée', color: 'error', variant: 'outlined' };
    case 'expired':
      return { label: 'Expirée', color: 'warning', variant: 'outlined' };
    case 'failed':
      return { label: 'Échec', color: 'error', variant: 'outlined' };
    default:
      return { label: 'À valider', color: 'warning', variant: 'outlined' };
  }
}

export function getPaymentStatusMeta(status: PaymentStatus, bookingStatus?: BookingStatus): ReservationChipMeta {
  if (bookingStatus === 'cancelled' && (status === 'failed' || status === 'expired')) {
    return { label: 'Paiement annulé', color: 'warning', variant: 'outlined' };
  }

  switch (status) {
    case 'paid':
      return { label: 'Reçu', color: 'success', variant: 'filled' };
    case 'refunded':
      return { label: 'Remboursé', color: 'info', variant: 'filled' };
    case 'failed':
      return { label: 'Échoué', color: 'error', variant: 'outlined' };
    case 'expired':
      return { label: 'Expiré', color: 'warning', variant: 'outlined' };
    default:
      return { label: 'En attente', color: 'warning', variant: 'outlined' };
  }
}

export function getAttendanceStatusMeta(status: AttendanceStatus, bookingStatus?: BookingStatus): ReservationChipMeta | null {
  if (bookingStatus && ['cancelled', 'expired', 'failed'].includes(bookingStatus)) {
    return null;
  }

  switch (status) {
    case 'reported_absent':
      return { label: 'Absence signalée', color: 'secondary', variant: 'outlined' };
    case 'attended':
      return { label: 'Présent', color: 'success', variant: 'filled' };
    case 'no_show':
      return { label: 'No-show', color: 'error', variant: 'filled' };
    default:
      return { label: 'Attendu', color: 'default', variant: 'outlined' };
  }
}

export function getRefundStatusMeta(status: RefundStatus): ReservationChipMeta | null {
  switch (status) {
    case 'pending':
      return { label: 'À traiter', color: 'warning', variant: 'filled' };
    default:
      // En vue opérationnelle, on masque les remboursements résolus ou non concernés.
      return null;
  }
}

export function getPartialRefundStatusMeta(partialRefundState: string | null): ReservationChipMeta | null {
  switch (partialRefundState) {
    case 'pending_customer_iban':
      return { label: 'IBAN client manquant', color: 'warning', variant: 'outlined' };
    case 'pending_manual':
      return { label: 'Remboursement à traiter', color: 'info', variant: 'filled' };
    case 'processed':
    case 'refunded':
      return { label: 'Remboursement effectué', color: 'success', variant: 'filled' };
    case 'refused':
      return { label: 'Remboursement refusé', color: 'error', variant: 'outlined' };
    default:
      return null;
  }
}
